﻿/**
 * HeaderSearch
 * ----------
 * Purpose:
 * - Site-wide search box rendered in the header with a type-ahead (MUI Autocomplete).
 * - Collapsed state: bounded width in the header; Expanded state: full-bleed overlay on mobile.
 *
 * Key behaviors:
 * - Server-driven suggestions (`suggest`) -> we do not re-filter on the client.
 * - Accessibility: standard input semantics; result list is keyboard navigable
 *
 * Styling strategy:
 * - All visual rules are centralized in `search/styles` as MUI `sx` snippets.
 * - Expanded mode uses Paper overrides to stretch the dropdown to 100vw above the page content.
 */

import { OPTION_TYPES } from '@/components/content/Header/constants';
import {
	renderGroupFunction,
	renderOptionFunction,
} from '@/components/content/Header/parts/SearchAutocomplete';

import {
	autocompleteSX,
	headerSearchContainerSX,
	headerSearchIconArrowBackSX,
	headerSearchIconSX,
	headerSearchSX,
	headerSearchTextfieldInputPropsSX,
	headerSearchTextfieldInputSlotPropsSX,
	headerSearchTextfieldSX,
} from '@/components/content/Header/styles/search';
import { callRegex, getFormattedOptions } from '@/components/content/Header/utils/search';
import { useSearchNavigation } from '@/data/Content/SearchNavigation';
import { useLocalization } from '@/data/Localization';
import { SuggestOption } from '@/data/types/Search';
import { comparator } from '@/utils/search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import {
	Autocomplete,
	Box,
	ClickAwayListener,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useGetCategories } from 'shared/custom/hooks/Categories';

export const HeaderSearch: FC<{ mobile?: boolean }> = () => {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [index, setIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('md'));
	const expand = isSm && open;
	const { navigation: categories } = useGetCategories();
	const { searchValue, suggest, onInputChange, onSubmit } = useSearchNavigation();
	const SearchNLS = useLocalization('SearchBar');
	const uniqueId = `type-ahead-site-search-desktop`;
	const MIN_CHARACTERS_SEARCH = 2;

	const options = useMemo<SuggestOption[]>(
		() => getFormattedOptions(categories, suggest, comparator),
		[suggest, categories]
	);

	const nameList: string[] = useMemo(
		() =>
			(
				(suggest?.[0]?.entry ?? []) as Array<{
					label?: string;
					name?: string;
					term?: string;
				}>
			).map((e: any) => e.label ?? e.name ?? e.term ?? ''),
		[suggest]
	);

	const OPTION_TYPES_TITLE: Record<string, string> = {
		[OPTION_TYPES.Brand]: SearchNLS.brands.t(),
		[OPTION_TYPES.Keyword]: '',
		[OPTION_TYPES.Category]: SearchNLS.category.t(),
	};

	const handleOnSubmit = (searchTerm: string) => {
		onSubmit({ label: searchTerm });
		setOpen(false);
	};

	const onKeyDownInSearchBar = (e: {
		key: string;
		preventDefault: () => void;
		code: string;
		currentTarget: { value: any };
		defaultMuiPrevented?: boolean;
	}) => {
		const len = nameList ? nameList.length : 0;
		let str = '';
		if (e.key === 'ArrowUp') {
			e.preventDefault();

			if (index === 0) {
				return;
			}
			setIndex(index - 1);
			if (nameList) {
				str = callRegex(nameList[index - 1]);
				setInputValue(str);
			}
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();

			if (index === len - 1) {
				setIndex(0);
				if (nameList) {
					str = callRegex(nameList[0]);
					setInputValue(str);
				}
				return;
			}
			setIndex(index + 1);
			if (nameList) {
				str = callRegex(nameList[index + 1]);
				setInputValue(str);
			}
		}

		if ((e.key === 'Enter' || e.code === 'Enter') && e.currentTarget.value) {
			e.preventDefault();
			e.defaultMuiPrevented = true;
			handleOnSubmit(e.currentTarget.value);
		}
	};

	const filterOptions = (options: SuggestOption[]) => {
		const keywords: SuggestOption[] = [];
		const brands: SuggestOption[] = [];
		const categories: SuggestOption[] = [];

		for (const op of options) {
			if (op.identifier === OPTION_TYPES.Keyword) keywords.push(op);
			else if (op.identifier === OPTION_TYPES.Brand) brands.push(op);
			else if (op.identifier === OPTION_TYPES.Category) categories.push(op);
		}

		return [
			...keywords,
			...(brands.length ? brands : [{ label: '', identifier: OPTION_TYPES.Brand }]),
			...(categories.length ? categories : [{ label: '', identifier: OPTION_TYPES.Category }]),
		];
	};
	useEffect(() => {
		if (expand && inputRef.current) {
			inputRef.current.focus();
		}
	}, [expand]);

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<Box sx={autocompleteSX.parentContainer}>
				<Box sx={headerSearchContainerSX(theme, expand)}>
					<Autocomplete
						freeSolo
						disablePortal
						disableClearable
						inputValue={inputValue}
						onChange={(_, item) => {
							if (typeof item === 'string') {
								handleOnSubmit(item);
							}
						}}
						onInputChange={(_, v, reason) => {
							if (reason !== 'input') return;
							setInputValue(v);
							const q = v?.trim() ?? '';
							if (q.length >= MIN_CHARACTERS_SEARCH) {
								onInputChange(_, v);
							}
						}}
						options={options}
						groupBy={(option) => option.identifier}
						filterOptions={(options, state) => {
							const q = state.inputValue?.trim() ?? '';
							const returnOptions = filterOptions(options);

							return q.length >= MIN_CHARACTERS_SEARCH ? returnOptions : [];
						}}
						renderOption={renderOptionFunction(inputValue, onSubmit)}
						renderGroup={renderGroupFunction(OPTION_TYPES_TITLE)}
						sx={headerSearchSX({ theme, expand })}
						id={uniqueId}
						open={open}
						onOpen={() => setOpen(true)}
						onClose={(_e, reason) => {
							if (reason === 'blur' || reason === 'toggleInput') return;
							setOpen(false);
						}}
						blurOnSelect={false}
						clearOnBlur={false}
						disableCloseOnSelect
						componentsProps={{
							paper: { sx: expand ? autocompleteSX.listContainer : {} },
						}}
						renderInput={({ inputProps, ...params }) => (
							<TextField
								inputRef={inputRef}
								inputProps={{ ...inputProps, 'data-testid': uniqueId }}
								{...params}
								placeholder={SearchNLS.searchBarPlaceholder.t()}
								sx={headerSearchTextfieldSX(expand)}
								InputProps={{
									...params.InputProps,
									sx: headerSearchTextfieldInputPropsSX(expand),
									slotProps: {
										input: {
											role: undefined,
											'aria-expanded': undefined,
											sx: expand ? headerSearchTextfieldInputSlotPropsSX() : {},
										},
									},
									size: 'small',
									type: 'input',
									onKeyDown: onKeyDownInSearchBar,
									startAdornment: expand ? (
										<IconButton
											onClick={() => setOpen(false)}
											edge="start"
											disableFocusRipple
											sx={headerSearchIconArrowBackSX()}
										>
											<ArrowBackIcon />
										</IconButton>
									) : null,
									endAdornment: (
										<InputAdornment
											position="end"
											sx={{
												backgroundColor: 'primary.main',
											}}
										>
											<IconButton
												sx={headerSearchIconSX(theme, expand)}
												aria-label={SearchNLS.SearchField.t()}
												onClick={() => handleOnSubmit(searchValue)}
												edge="end"
											>
												<SearchIcon />
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						)}
						PaperComponent={({ children }) => (
							<Paper sx={autocompleteSX.paper}>
								<Box
									sx={
										inputValue.length >= MIN_CHARACTERS_SEARCH && options.length > 0
											? autocompleteSX.paperContainer
											: undefined
									}
								>
									{children}
								</Box>
							</Paper>
						)}
						slotProps={{
							popper: {
								sx: autocompleteSX.poper,
							},
						}}
					/>
				</Box>
			</Box>
		</ClickAwayListener>
	);
};
